import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, Trash2, Wallet, AlertTriangle, CheckCircle2, Inbox, Clock } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";
import { METHOD_ORDER, slotComplete } from "@/constants/paymentMethods";

const inputCls =
  "w-full rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-bg)] px-4 py-2.5 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10 transition-all";

const REQUEST_LABEL = {
  all: "Primary + backup payment details",
  primary: "Primary payment method",
  backup: "Backup payment method",
  replacement: "Updated payment details",
};

// Owner-approval moderation states per slot (backend: slot_states)
const STATE_CHIPS = {
  approved: { text: "Approved", cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" },
  pending: { text: "Pending owner approval", cls: "border-amber-500/30 bg-amber-500/10 text-amber-300" },
  replacement_pending: { text: "Approved — update pending approval", cls: "border-amber-500/30 bg-amber-500/10 text-amber-300" },
  rejected: { text: "Not approved", cls: "border-rose-500/30 bg-rose-500/10 text-rose-300" },
};

const emptySlot = () => ({ method: "", fields: {} });

// One method card: select rail → only that rail's fields render (no irrelevant inputs).
const SlotEditor = ({ testid, title, slot, complete, state, rejected, onChange, onSave, onClear, busy, methods, optional = false }) => {
  const spec = slot.method ? methods[slot.method] : null;
  const chip = STATE_CHIPS[state];
  return (
    <div data-testid={`${testid}-card`} className={`rounded-3xl border p-5 sm:p-6 ${state === "approved" && complete ? "border-emerald-500/25 bg-emerald-500/5" : "border-[var(--pv-border)] bg-[var(--pv-subtle)]"}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-display text-sm font-bold uppercase tracking-wider text-[var(--pv-text-3)]">{title}</p>
        {chip && (
          <span data-testid={`${testid}-status`} className={`flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase ${chip.cls}`}>
            {state === "pending" || state === "replacement_pending" ? <Clock className="h-3 w-3" /> : null}
            {chip.text}
          </span>
        )}
        {!chip && slot.method && (
          <span data-testid={`${testid}-status`} className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase ${complete ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-amber-500/30 bg-amber-500/10 text-amber-300"}`}>
            {complete ? "Complete" : "Incomplete"}
          </span>
        )}
      </div>
      {optional && (
        <p className="mt-2 text-[11px] leading-relaxed text-[var(--pv-text-4)]">
          Optional — add a backup method in case we're unable to send payment through your primary method.
        </p>
      )}
      {state === "rejected" && (
        <p data-testid={`${testid}-rejected-note`} className="mt-2 flex items-start gap-1.5 text-[11px] font-semibold leading-relaxed text-rose-300">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          This method was not approved{rejected?.note ? `: ${rejected.note}` : ""} — please submit a different method.
        </p>
      )}
      {/* rejected REPLACEMENT over an approved method: the active version stays, but the worker
          must still see that their change was declined and why (rejection clears on resubmit) */}
      {state === "approved" && rejected && (
        <p data-testid={`${testid}-rejected-note`} className="mt-2 flex items-start gap-1.5 rounded-xl border border-amber-500/25 bg-amber-500/5 p-2.5 text-[11px] font-semibold leading-relaxed text-amber-300">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Your updated {rejected.method && (methods[rejected.method]?.label || rejected.method)} details weren't approved{rejected.note ? `: ${rejected.note}` : ""} — your previous approved method is still active.
        </p>
      )}
      {(state === "approved" || state === "replacement_pending") && (
        <p className="mt-2 text-[11px] leading-relaxed text-[var(--pv-text-4)]">
          Editing re-submits the method for owner approval — your currently approved details stay active until the change is approved.
        </p>
      )}
      <div className="mt-4 grid gap-3">
        <select
          data-testid={`${testid}-method`}
          value={slot.method}
          onChange={(e) => onChange({ method: e.target.value, fields: {} })}
          className={inputCls}
        >
          <option value="">Select payment method…</option>
          {/* retired methods (e.g. Bank Transfer) stay labeled in history but can't be picked */}
          {METHOD_ORDER.filter((m) => methods[m] && !methods[m].retired).map((m) => (
            <option key={m} value={m}>{methods[m].label}</option>
          ))}
        </select>
        {spec && spec.fields.map((f) => (
          <div key={f.key}>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">
              {f.label} {f.required && <span className="text-amber-400">*</span>}
            </label>
            {f.key === "instructions" || f.key === "bank_details" || f.key === "notes" ? (
              <textarea
                data-testid={`${testid}-field-${f.key}`}
                rows={2}
                value={slot.fields[f.key] || ""}
                onChange={(e) => onChange({ ...slot, fields: { ...slot.fields, [f.key]: e.target.value } })}
                className={inputCls}
              />
            ) : (
              <input
                data-testid={`${testid}-field-${f.key}`}
                value={slot.fields[f.key] || ""}
                onChange={(e) => onChange({ ...slot, fields: { ...slot.fields, [f.key]: e.target.value } })}
                className={inputCls}
              />
            )}
          </div>
        ))}
        {spec && !complete && (
          <p className="text-[10px] font-semibold text-amber-300/80">Fill the fields marked * for this method to count as complete.</p>
        )}
        <div className="mt-1 flex items-center gap-2">
          <button
            data-testid={`${testid}-save`}
            onClick={onSave}
            disabled={busy || !slot.method}
            className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-5 py-2.5 text-xs font-bold text-white transition-colors hover:bg-cyan-600 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Submit for approval
          </button>
          {onClear && slot.method && (
            <button data-testid={`${testid}-clear`} onClick={onClear} disabled={busy} className="flex items-center gap-1 rounded-full border border-[var(--pv-border)] px-4 py-2 text-xs font-bold text-[var(--pv-text-3)] hover:bg-rose-500/10 hover:text-rose-400 disabled:opacity-50">
              <Trash2 className="h-3 w-3" /> Remove backup
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Worker's own Payment Details home (Profile → Payment Details). Also answers outstanding requests.
// Every new/changed method goes through owner approval before it can be used for payouts.
export const PaymentDetailsForm = () => {
  const [data, setData] = useState(null);
  const [primary, setPrimary] = useState(emptySlot());
  const [backup, setBackup] = useState(emptySlot());
  const [backupOpen, setBackupOpen] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/my/payment-details`, { headers: authHeaders() })
      .then((r) => {
        setData(r.data);
        // prefill from the PENDING submission when one exists (worker continues editing it),
        // otherwise from the active approved version
        const p = (r.data.pending || {}).primary || r.data.primary;
        const b = (r.data.pending || {}).backup || r.data.backup;
        setPrimary(p ? { method: p.method, fields: { ...p.fields } } : emptySlot());
        setBackup(b ? { method: b.method, fields: { ...b.fields } } : emptySlot());
        if ((r.data.pending || {}).backup) setBackupOpen(true);
      })
      .catch(() => toast.error("Could not load your payment details."));
  }, []);
  useEffect(load, [load]);

  if (!data) return <p className="py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-cyan-400" /></p>;
  const methods = data.methods;
  const states = data.slot_states || {};
  const rejected = data.rejected || {};

  const save = async (slotKey) => {
    const slot = slotKey === "primary" ? primary : backup;
    if (!slot.method) return toast.error("Choose a payment method first.");
    setBusy(slotKey);
    try {
      const r = await axios.put(`${API}/my/payment-details`, { [slotKey]: { method: slot.method, fields: slot.fields } }, { headers: authHeaders() });
      const done = r.data.requests_completed || 0;
      const submitted = (r.data.submitted_for_approval || []).length > 0;
      toast.success(
        done > 0
          ? "Submitted — pending owner approval. Your outstanding request is now completed."
          : submitted
            ? "Submitted — pending owner approval."
            : "Payment details saved."
      );
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Could not save.");
    } finally { setBusy(null); }
  };

  const clearBackup = async () => {
    setBusy("backup");
    try {
      await axios.put(`${API}/my/payment-details`, { clear_backup: true }, { headers: authHeaders() });
      toast.success("Backup method removed.");
      setBackup(emptySlot());
      setBackupOpen(false);
      load();
    } catch { toast.error("Could not remove the backup method."); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="payment-details-form">
      {(data.pending_requests || []).length > 0 && (
        <div data-testid="payment-request-banner" className="mb-5 rounded-3xl border border-amber-500/30 bg-amber-500/10 p-5">
          <p className="flex items-center gap-2 text-sm font-bold text-amber-300">
            <Inbox className="h-4 w-4" /> Payment details requested by Melto Studios
          </p>
          <ul className="mt-2 space-y-1">
            {data.pending_requests.map((r) => (
              <li key={r.id} data-testid={`payment-request-${r.id}`} className="text-xs font-semibold text-amber-200/80">
                • {REQUEST_LABEL[r.kind] || r.kind} required — requested {new Date(r.requested_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                {r.note && <span className="text-amber-200/60"> — “{r.note}”</span>}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-amber-200/60">Fill the matching section below — once submitted, the owner reviews it.</p>
        </div>
      )}

      {data.status === "pending" && (
        <div data-testid="payment-pending-hint" className="mb-5 flex items-start gap-2.5 rounded-3xl border border-amber-500/30 bg-amber-500/10 p-4">
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
          <p className="text-xs leading-relaxed text-amber-200/90">
            Pending owner approval — your payment details were submitted and will become active once the owner approves them.
          </p>
        </div>
      )}
      {(data.status === "missing" || data.status === "partial") && (data.pending_requests || []).length === 0 && (
        <div data-testid="payment-incomplete-hint" className="mb-5 flex items-start gap-2.5 rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] p-4">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
          <p className="text-xs leading-relaxed text-[var(--pv-text-3)]">
            {data.status === "missing"
              ? "No approved payment details on file yet — submit your primary payment method so Melto Studios can pay you. It activates once the owner approves it. A backup is optional."
              : "Your primary payment method needs a little more information — check the card below."}
          </p>
        </div>
      )}
      {data.status === "complete" && (
        <p data-testid="payment-complete-hint" className="mb-5 flex items-center gap-2 rounded-3xl border border-emerald-500/25 bg-emerald-500/10 p-4 text-xs font-bold text-emerald-300">
          <CheckCircle2 className="h-4 w-4" /> Payment setup complete — your primary method is approved{data.backup_complete ? " with a backup" : ""}.
        </p>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        <SlotEditor testid="primary-payment" title="Primary method" slot={primary} complete={slotComplete(primary, methods)} state={states.primary} rejected={rejected.primary} methods={methods} onChange={setPrimary} onSave={() => save("primary")} busy={busy === "primary"} />
        {data.backup_set || backup.method || backupOpen ? (
          <SlotEditor testid="backup-payment" title="Backup method (optional)" slot={backup} complete={slotComplete(backup, methods)} state={states.backup} rejected={rejected.backup} methods={methods} onChange={setBackup} onSave={() => save("backup")} onClear={data.backup_set ? clearBackup : null} busy={busy === "backup"} optional />
        ) : (
          <div data-testid="backup-payment-collapsed" className="flex flex-col items-start justify-center rounded-3xl border border-dashed border-[var(--pv-border-strong)] bg-[var(--pv-subtle)] p-6">
            <p className="font-display text-sm font-bold uppercase tracking-wider text-[var(--pv-text-3)]">Backup method (optional)</p>
            <p className="mt-2 text-xs leading-relaxed text-[var(--pv-text-4)]">
              Optional — add a backup method in case we're unable to send payment through your primary method. It also activates after owner approval.
            </p>
            <button
              data-testid="add-backup-btn"
              onClick={() => setBackupOpen(true)}
              className="mt-4 rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-5 py-2.5 text-xs font-bold text-[var(--pv-text-3)] transition-colors hover:border-cyan-500/30 hover:text-cyan-400"
            >
              + Add backup method
            </button>
          </div>
        )}
      </div>

      {data.updated_at && (
        <p data-testid="payment-details-updated" className="mt-4 text-[11px] font-medium text-[var(--pv-text-4)]">
          <Wallet className="mr-1 inline h-3 w-3" /> Last updated {new Date(data.updated_at).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
        </p>
      )}
      <p className="mt-1 text-[11px] text-[var(--pv-text-4)]">Only the owner can see these details. New or changed methods activate after owner approval; historical payments always keep the method that was actually used at the time.</p>
    </div>
  );
};
